/**
 * /case/:cid/video-followup — landing that arrives from the 48h WhatsApp
 * nudge. Shows a compact "you had a written report, here's the follow-up
 * offer" panel and hands off to the standard payment + slot-picker flow
 * once the patient taps "Book my follow-up".
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Video, Loader2, CheckCircle2, ArrowRight, Stethoscope, ShieldCheck, Clock } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function VideoFollowup() {
  const { cid } = useParams();
  const nav = useNavigate();
  const { user, token } = useAuth();
  const [parentCase, setParentCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    document.title = "Book 20-min video follow-up · NeuroOpinion India";
    // Public info fetch — case UUID is unguessable so this endpoint is safe
    // to hit unauthenticated (WhatsApp nudge recipients haven't necessarily
    // clicked through to login yet).
    axios.get(`${API_BASE}/api/consultations/${cid}/followup-context`)
      .then((r) => setParentCase(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load your case"))
      .finally(() => setLoading(false));
  }, [cid]);

  const book = async () => {
    if (!user || !token) {
      nav(`/login?as=patient&next=/case/${cid}/video-followup`);
      return;
    }
    if (user.role !== "patient") {
      toast.error("Switch to your patient account to book a follow-up");
      return;
    }
    setBooking(true);
    try {
      const r = await api.post(`/consultations/${cid}/book-video-followup`);
      const newCid = r.data?.id;
      if (!newCid) throw new Error("Booking failed");
      toast.success("Follow-up case created — pick your slot next");
      // Deep-link straight into the patient dashboard's payment wizard for
      // the new case.
      nav(`/patient?case=${newCid}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not book — please try again");
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="w-8 h-8 text-violet-600 animate-spin" />
      </div>
    );
  }

  const shortId = parentCase?.case_ref || (cid || "").slice(0, 8).toUpperCase();
  const doctorName = parentCase?.doctor_name || "your specialist";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-violet-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
          &larr; Back to NeuroOpinion
        </Link>

        <Card className="mt-4 p-6 sm:p-8 border-slate-200 bg-white shadow-xl rounded-2xl" data-testid="video-followup-panel">
          <div className="flex items-start gap-4">
            <span className="w-12 h-12 rounded-full bg-violet-100 text-violet-700 grid place-items-center shrink-0">
              <Video className="w-6 h-6" />
            </span>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-700">
                Follow-up available
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mt-1 leading-tight">
                Book a 20-minute video follow-up with Dr. {doctorName?.replace(/^Dr\.?\s*/i, "")}
              </h1>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                It&apos;s been a couple of days since your written report on case
                <span className="font-mono font-semibold"> {shortId}</span>. Many
                patients benefit from a short live call — ask questions, discuss
                test results and get personalised next steps in real time.
              </p>
            </div>
          </div>

          {/* Value bullets */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <ValuePill icon={Stethoscope} label="Same neurologist" sub="Who reviewed your case" />
            <ValuePill icon={Clock} label="20 minutes · HD" sub="On Jitsi Meet — no install" />
            <ValuePill icon={ShieldCheck} label="Pay only if you book" sub="Cancel free up to 6 hrs before" />
          </div>

          {/* Price block */}
          <div className="mt-6 rounded-xl border-2 border-violet-200 bg-violet-50/60 p-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-violet-800">Follow-up fee</div>
              <div className="mt-0.5 font-heading text-2xl font-bold text-slate-900" data-testid="followup-price">
                ₹2,000
              </div>
              <div className="text-[11px] text-slate-500">International patients pay in USD at checkout.</div>
            </div>
            <Button
              onClick={book}
              disabled={booking}
              size="lg"
              className="h-11 px-5 bg-violet-600 hover:bg-violet-700 text-white shadow-lg"
              data-testid="video-followup-book-btn"
            >
              {booking ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Video className="w-4 h-4 mr-2"/>}
              {booking ? "Creating your follow-up…" : "Book my follow-up"}
              {!booking && <ArrowRight className="w-4 h-4 ml-1"/>}
            </Button>
          </div>

          {/* Fine print */}
          <div className="mt-5 pt-4 border-t border-slate-100 text-xs text-slate-500 leading-relaxed">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5"/>
              After payment you&apos;ll pick a slot from the specialist&apos;s open calendar
              and get a WhatsApp reminder 15 minutes before the call.
            </div>
            <div className="mt-2 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5"/>
              This is optional — your original written second opinion remains
              accessible any time from your patient dashboard.
            </div>
          </div>
        </Card>

        <p className="mt-4 text-center text-[11px] text-slate-400">
          Prefer to skip? Ignore this page — no charge, no obligation.
        </p>
      </div>
    </div>
  );
}

function ValuePill({ icon: Icon, label, sub }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="w-8 h-8 rounded-lg bg-violet-100 text-violet-700 grid place-items-center">
        <Icon className="w-4 h-4"/>
      </div>
      <div className="mt-2 text-sm font-semibold text-slate-900">{label}</div>
      <div className="text-[11px] text-slate-500 leading-tight">{sub}</div>
    </div>
  );
}
